// updateChat — chat's `update` under the module contract (modules/module.ts):
// decide, then fold the decided events, yielding a `Step` (next model, effects
// for chat's interpreter, Signals for the root to route). Pure. A refusal stays
// an explicit `ChatError` (ADR-1). See chat-module-plan.md step S3.

import { match, Result } from "@bilda/utils";
import type { Inbound, Step, Update } from "../module.js";
import type { Signal } from "../signal.js";
import type { ChatCommand } from "./command.js";
import { decideChat } from "./decide.js";
import type { ChatDecision, ChatEffect } from "./effect.js";
import type { ChatError } from "./error.js";
import { evolveChatAll } from "./evolve.js";
import type { ChatState } from "./state.js";

export type ChatStep = Step<ChatState, ChatEffect>;

export const updateChat: Update<
    ChatState,
    ChatCommand,
    ChatEffect,
    ChatError
> = (model, msg) =>
    Result.map(
        ({ events, effects, signals }: ChatDecision): ChatStep => ({
            model: evolveChatAll(model, events),
            effects,
            signals,
        }),
    )(decideChat(model, msg));

// Chat owns stop requests from outside its surface (the keyboard door); the
// decider refuses one with nothing in flight (`stop_while_idle`). Every other
// Signal is another owner's.
export const inboundChat: Inbound<ChatCommand> = match<
    Signal,
    ChatCommand | undefined
>({
    "Signal.StopRequested": ({ conversationId }) => ({
        type: "requested_stop",
        conversationId,
    }),
    "Signal.NoticeRaised": () => undefined,
    "Signal.WorldChanged": () => undefined,
    "Signal.FrameRequested": () => undefined,
    "Signal.ConversationStarted": () => undefined,
    "Signal.PendingGroundingFlushed": () => undefined,
    "Signal.ProposalLanded": () => undefined,
});
